import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { getErrorId, getInfoId, mergeRefs, onEnterOrSpacePress, } from '@hivebrite/utils';
import { Box } from "../../../box/src";
import { CATEGORIES, FontAwesomeIcon } from "../../../font-awesome-icon/src";
import { focusOutline, responsive, useTheme } from "../../../theme/src";
const CloseIcon = () => (_jsx(FontAwesomeIcon, { height: "16px", iconName: "xmark", iconType: CATEGORIES.SOLID }));
const Component = ({ ariaDescribedBy, ariaLabel, as, autoComplete = 'off', clearable, css, dataTestId, disabled, error, height, icon: IconComponent, iconIsClickable, id, info, minHeight, name, onBlur, onChange, onFocus, placeholder, required, type, value, variant, }, ref) => {
    var _a, _b;
    const [isFocus, setIsFocus] = useState(false);
    const inputRef = useRef(null);
    const theme = useTheme();
    const themeInput = theme.input[variant];
    const iconPaddingValue = (_a = theme.space[4] + theme.input[variant].iconSize) !== null && _a !== void 0 ? _a : 24;
    const isClearable = useMemo(() => {
        if (clearable && !disabled) {
            if (type === 'number') {
                return !!value || value === 0;
            }
            if (value === undefined) {
                return false;
            }
            return (value === null || value === void 0 ? void 0 : value.toString().trim()) !== '';
        }
        return false;
    }, [clearable, disabled, type, value]);
    const handleBlur = useCallback((e) => {
        setIsFocus(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }, [onBlur]);
    const handleFocus = useCallback((e) => {
        setIsFocus(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }, [onFocus]);
    const handleClearValue = useCallback(() => {
        if (isClearable && inputRef.current) {
            const event = new Event('change', { bubbles: true });
            if (inputRef.current instanceof HTMLInputElement ||
                inputRef.current instanceof HTMLTextAreaElement) {
                inputRef.current.value = '';
                inputRef.current.dispatchEvent(event);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(event);
            if (inputRef.current instanceof HTMLInputElement ||
                inputRef.current instanceof HTMLTextAreaElement) {
                inputRef.current.focus();
            }
        }
    }, [isClearable, onChange]);
    const hasError = Boolean(error);
    const errorId = useMemo(() => getErrorId(id), [id]);
    const hasInfo = Boolean(info);
    const infoId = useMemo(() => getInfoId(id), [id]);
    return (_jsxs(InputWrapper, { "$hasError": hasError, "$isFocus": isFocus, height: height, minHeight: minHeight, variant: variant, children: [IconComponent && (_jsx(IconWrapper, { "$iconIsClickable": iconIsClickable, "$iconPaddingValue": iconPaddingValue, height: themeInput.iconSize, variant: variant, width: themeInput.iconSize, children: _jsx(IconComponent, { height: "100%", size: "100%", width: "100%" }) })), _jsx(StyledInput, Object.assign({}, ((hasError || hasInfo || ariaDescribedBy) && {
                'aria-describedby': [
                    hasError && errorId,
                    hasInfo && infoId,
                    ariaDescribedBy,
                ]
                    .filter(Boolean)
                    .join(' '),
            }), (hasError && {
                'aria-invalid': hasError,
            }), (ariaLabel && {
                'aria-label': ariaLabel,
            }), { "$css": css, "$hasLeftIcon": Boolean(IconComponent), "$hasRightIcon": isClearable, "$iconPaddingValue": iconPaddingValue, "aria-required": required ? 'true' : undefined, as: as, autoComplete: autoComplete, "data-testid": dataTestId, disabled: disabled, id: id, name: name, onBlur: handleBlur, onChange: onChange, onFocus: handleFocus, placeholder: placeholder, ref: mergeRefs([ref, inputRef]), type: type, value: value, variant: variant })), isClearable && (_jsx(IconWrapper, { "$iconIsClickable": true, "$iconPaddingValue": iconPaddingValue, "$rightIcon": true, "aria-label": ((_b = window === null || window === void 0 ? void 0 : window.s__) === null || _b === void 0 ? void 0 : _b.call(window, 'Accessibility|Clean this input value')) ||
                    'Clean this input value', height: themeInput.iconSize, onClick: handleClearValue, onKeyPress: onEnterOrSpacePress(handleClearValue), role: "button", tabIndex: 0, variant: variant, width: themeInput.iconSize, children: _jsx(CloseIcon, {}) }))] }));
};
export const DefaultComponent = forwardRef(Component);
const getBoxShadow = ({ $hasError, $isFocus, theme, variant, }) => {
    if ($isFocus) {
        return `${theme.input[variant].focus.borderColor} 0 0 0 1px inset !important`;
    }
    if ($hasError) {
        return `${theme.input[variant].error.borderColor} 0 0 0 1px inset !important`;
    }
    return `${theme.input[variant].borderColor} 0 0 0 1px inset !important`;
};
const getInputStyle = ({ as, theme, variant }) => css `
  background: transparent !important;
  border-radius: 0 !important;
  border: 0 !important;
  color: ${theme.input[variant].color} !important;
  // To avoid zoom on iOS mobile devices, inputs must have font-size set to 16px on s & m
  font-size: ${theme.fontSizes[3]} !important;
  ${responsive('l') `
    font-size: ${theme.fontSizes[2]} !important;
  `}
  padding: 0 0 0 ${({ theme }) => theme.space[3] - 1}px !important;
  width: 100%;
  /* Textarea must have no height set to allow resize-y properly */
  ${as !== 'textarea' && 'height: 100% !important;'}
  ${as === 'textarea' && 'padding: 11px !important;'}
  ${as === 'textarea' && 'resize: vertical;'}

  ${({ $hasLeftIcon, $iconPaddingValue }) => $hasLeftIcon &&
    css `
      padding-left: ${$iconPaddingValue}px !important;
    `}

  ${({ $hasRightIcon, $iconPaddingValue }) => $hasRightIcon &&
    css `
      padding-right: ${$iconPaddingValue}px !important;
    `}

  &:focus {
    outline: none !important;
  }
`;
const IconWrapper = styled(Box) `
  ${focusOutline}

  ${({ $iconIsClickable, $iconPaddingValue, $rightIcon, theme, variant, }) => css `
    ${$iconIsClickable
    ? `
        cursor: pointer;
        pointer-events: all;
        `
    : `
        cursor: default;
        pointer-events: none;
      `}

    color: ${theme.input[variant].iconColor};
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    ${$rightIcon
    ? `
      right: ${$iconPaddingValue / 2}px;
      transform: translate(50%, -50%);
      `
    : `
      left: ${$iconPaddingValue / 2}px;
      transform: translate(-50%, -50%);
      `}
  `}
`;
const InputWrapper = styled(Box).attrs(({ height, theme, variant }) => ({
    height: height !== null && height !== void 0 ? height : theme.input[variant].height,
})) `
  ${({ minHeight, theme, variant }) => css `
    background-color: ${theme.input.bg};
    border-radius: ${theme.input[variant].borderRadius}px;
    box-shadow: ${getBoxShadow};
    ${minHeight && `min-height: ${minHeight}px;`}
    position: relative;
  `}
`;
const StyledInput = styled.input `
  ${({ $css }) => $css}
  resize: none;
  ${getInputStyle};

  &::placeholder {
    color: ${({ theme, variant }) => theme.input[variant].placeholder};
  }
`;
//# sourceMappingURL=DefaultComponent.js.map